import classNames from 'classnames';
import { Button as ReactAriaButton, type ButtonProps as ReactAriaButtonProps } from 'react-aria-components/Button';
import {
  Menu as ReactAriaMenu,
  MenuItem as ReactAriaMenuItem,
  MenuTrigger as ReactAriaMenuTrigger,
  Popover as ReactAriaPopover,
  type MenuItemProps as ReactAriaMenuItemProps,
  type MenuTriggerProps as ReactAriaMenuTriggerProps,
  type PopoverProps as ReactAriaPopoverProps,
} from 'react-aria-components/Menu';

import Icon from '../../../app/shared/components/Icon/Icon';
import css from './Menu.css';

type MenuProps = Pick<ReactAriaMenuTriggerProps, 'children'>;

export const Menu = (props: MenuProps) => {
  return <ReactAriaMenuTrigger {...props} />;
};

/** Without children the button is the "⋯" icon alone, so it has to be named some other way. */
type MenuButtonProps = Pick<ReactAriaButtonProps, 'isDisabled'> & { className?: string } & (
    | { children: React.ReactNode; 'aria-label'?: string }
    | { children?: never; 'aria-label': string }
  );

export const MenuButton = ({ children, className, ...props }: MenuButtonProps) => {
  return (
    <ReactAriaButton {...props} className={classNames(css.button, { [css.iconOnly]: !children }, className)}>
      {children || <Icon className={css.icon} name="options" />}
    </ReactAriaButton>
  );
};

type MenuContentProps = Pick<ReactAriaPopoverProps, 'offset'> & {
  children: React.ReactNode;
  className?: string;
};

export const MenuContent = ({ children, className, offset = -6 }: MenuContentProps) => {
  return (
    <ReactAriaPopover className={classNames(css.popover, className)} placement="bottom end" offset={offset}>
      <ReactAriaMenu className={css.menu}>{children}</ReactAriaMenu>
    </ReactAriaPopover>
  );
};

type MenuItemProps = Pick<ReactAriaMenuItemProps, 'id' | 'isDisabled' | 'onAction'> & {
  children: string;
};

export const MenuItem = ({ children, ...props }: MenuItemProps) => {
  return (
    <ReactAriaMenuItem {...props} className={css.item} textValue={children}>
      {children}
    </ReactAriaMenuItem>
  );
};
